import * as Config from '../../config';
import { default as Locale } from '../../utils/Locale';
import { GraphIcon } from '../../utils/Icon';
import * as MathLib from '../../utils/Math';
import * as UUID from '../../utils/Uuid';
import { AlertInfo, AlertType, AlertRecord, AlertCategories } from "../Types";
import { AlertLogic } from './AlertLogic';
import { ContentCategories } from "../../types";


export class UnstableDevice extends AlertLogic {
    static type = AlertType.UnstableDevice;
    static category = AlertCategories.EQUIPMENT_FAILURE;
    static contentCategories = [ ContentCategories.SYSTEM_ERROR ];
    static title = Locale.gettext('Unstable Device');

    getInfo(alert:AlertRecord = this.alert, config:Config.AllConfig = this.allConfigs):AlertInfo {
        // Same for all alert types
        let { body, device, alertData, firstReported } = this.getCommons(alert, config);
        
        // Varying across alert types
        let cardIllustration = 'pipes_emergency';
        let detailIcon = GraphIcon.getNodeIconName(device) ?? 'pump';
        
        let type;
        let min;
        let max;
        switch (device.DeviceType) {
            case 'PHProbe':
                type = 'pH';
                min = alertData?.MinPH;
                max = alertData?.MaxPH;
                break;
            case 'ORPProbe':
                type = 'ORP';
                min = alertData?.MinORP;
                max = alertData?.MaxORP;
                break;
            case 'ChlorineSensor':
                type = 'ppm';
                min = alertData?.MinChlorine;
                max = alertData?.MaxChlorine;
                break;
            }
        
        const detailDescription2 = this.locale.gettext(
            'Over the last twenty minutes, the reading has varied between a minimum of %2$s %1$s and maximum of %3$s %1$s.',
            type,
            MathLib.formatAccuracy(min, 0.01),
            MathLib.formatAccuracy(max, 0.01)
        );

        const alertInfo = AlertInfo.fromCardConfig({
            cardTitle: this.locale.gettext('Unstable Device'),
            cardDescription: this.locale.gettext('%1$s is reading unstable values', device.Name),
            cardIllustration: cardIllustration,
            detailIcon: detailIcon,
            detailDescription: this.locale.gettext('%1$s is reading unstable values.', device.Name),
            detailDescription2,
            detailDescription3: this.locale.gettext('Please check to ensure the wiring and placement of the probe is correct.  You might also need to clean the sensor.'),
            actionButtonTitle: false,
            resolveSummary: this.locale.gettext('%1$s has normalized', device.Name)
        });
        alertInfo.contentCategories = UnstableDevice.contentCategories;
        return alertInfo;
    } 

    static mock(type:AlertType, config:Config.AllConfig):AlertRecord {
        let device = config.Config.Devices.find(dev => dev.DeviceType === "PHProbe");
        
        return device ? new AlertRecord({
            id: Math.floor(Math.random() * 100000),
            uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "ALRT" + type),
            siteId: Math.floor(Math.random() * 100000),
            deviceId: 1,
            entityUuid: device?.UUID,
            bodyUuid: config.Config.BodiesOfWater[0]?.UUID,
            type: type,
            data: {
                MinPH: 6.12,
                MaxPH: 7.33
            }
        }) : null;
    }
}